/**
 * The till locks itself when nobody is using it.
 *
 * A PIN swap only makes the record honest if people use it, and at eight
 * o'clock on a Friday nobody opens a sheet to hand over a till — they walk up
 * and tap whatever is on the screen. So a terminal left alone for a couple of
 * minutes covers itself with a keypad, and the next tap on it is somebody
 * saying who they are. Whoever that is, their name is on what happens next.
 *
 * WHY IT SURVIVES A RELOAD
 *
 * The lock is written to the device, not held in memory. A lock that pulling
 * the page down released would be a lock anyone could open with one gesture.
 *
 * WHY THE DELAY IS PER TABLET
 *
 * The one on the bar is used every twenty seconds and can wait long; the one
 * by the door is walked past by the public and should not. It is a fact about
 * where the tablet stands, like which till it is, so it is remembered there
 * and survives a sign-out.
 *
 * WHY THERE IS A WAY OUT THAT IS NOT A PIN
 *
 * An owner who never put themselves on the payroll has no PIN, and a tablet
 * whose staff have none set would otherwise be a brick. Signing out is always
 * offered underneath; it costs a password, which is the point.
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { usePinSignIn, useSession } from '@restaurant/api-client';
import { ActionError, Button, NumberPad } from '@restaurant/ui';

import { setLocked, useLocked, useLockMinutes } from './lock-store';
import { useConnection } from './offline/connection';
import { anyRemembered, forgetPins, rememberPin, unlockOffline } from './offline/pins';

const PIN_MAX = 8;

/** Anything a person does to the screen. Not mouse movement: a tablet has none. */
const ACTIVITY = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

/**
 * Watches for the terminal going quiet, and covers it when it does.
 *
 * Idle is measured against the wall clock rather than a timer that is reset,
 * because a tablet that sleeps suspends its timers: one put down for an hour
 * would wake up still two minutes from locking.
 */
export function IdleLock() {
  const [minutes] = useLockMinutes();
  const locked = useLocked();

  useEffect(() => {
    if (minutes === 0 || locked) return;
    let last = Date.now();
    const touched = () => {
      last = Date.now();
    };
    for (const name of ACTIVITY) window.addEventListener(name, touched, { passive: true });
    const check = () => {
      if (Date.now() - last >= minutes * 60_000) setLocked(true);
    };
    const id = window.setInterval(check, 5_000);
    document.addEventListener('visibilitychange', check);
    return () => {
      for (const name of ACTIVITY) window.removeEventListener(name, touched);
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', check);
    };
  }, [minutes, locked]);

  // Covering the screen is not enough: the floor underneath is still there for
  // a keyboard's Tab and a screen reader. So the app is made inert and the
  // keypad is put outside it, where it is the only thing left to reach.
  useEffect(() => {
    const root = document.getElementById('root');
    if (!root || !locked) return;
    root.inert = true;
    return () => {
      root.inert = false;
    };
  }, [locked]);

  if (!locked) return null;
  return createPortal(<LockScreen />, document.body);
}

function LockScreen() {
  // With no connection the server cannot check a PIN, so the tablet does
  // (offline/pins.ts), against the PINs that have unlocked it before.
  return useConnection() === 'offline' ? <OfflineLockScreen /> : <OnlineLockScreen />;
}

function OnlineLockScreen() {
  const swap = usePinSignIn();
  const session = useSession();
  const [pin, setPin] = useState('');

  function unlock() {
    // The promise, not `mutate`'s callbacks. A successful swap drops the cache
    // and the shell unmounts everything under it while it reloads who is
    // signed in � this included � and callbacks passed to `mutate` are not
    // called for a component that has gone. The promise settles regardless,
    // and the lock lives on the device, so releasing it from here is safe.
    swap.mutateAsync({ pin }).then(
      (tokens) => {
        // The server said yes: remember it for when there is no connection.
        void rememberPin(pin, tokens);
        setLocked(false);
      },
      () => setPin(''),
    );
  }

  return (
    <div className="lock" role="dialog" aria-modal="true" aria-labelledby="lock-title">
      <div className="lock__card">
        <h1 id="lock-title" className="lock__title">
          Who is using the till?
        </h1>
        <p className="lock__sub">Enter your PIN. Whatever happens next is under your name.</p>

        <div
          className="pin__dots"
          aria-live="polite"
          aria-label={`${pin.length} digits entered`}
        >
          {Array.from({ length: Math.max(4, pin.length) }, (_, i) => (
            <span key={i} className={`pin__dot${i < pin.length ? ' is-on' : ''}`} />
          ))}
        </div>

        <NumberPad
          value={pin}
          decimals={0}
          label="PIN"
          onChange={(next) => setPin(next.replace(/\D/g, '').slice(0, PIN_MAX))}
        />

        <ActionError error={swap.error} action="Unlocking the till" />

        <Button
          variant="primary"
          size="lg"
          block
          loading={swap.isPending}
          disabled={pin.length < 4}
          onClick={unlock}
        >
          Unlock
        </Button>

        <button
          type="button"
          className="lock__out"
          onClick={() => {
            setLocked(false);
            forgetPins();
            session.signOut();
          }}
        >
          No PIN? Sign out and use a password
        </button>
      </div>
    </div>
  );
}

/**
 * The lock with no connection. Only PINs that have unlocked this tablet
 * online in the last fortnight open it, and there is no signing out: nobody
 * could sign back in until the line is back, and the tablet could not sell.
 */
function OfflineLockScreen() {
  const [pin, setPin] = useState('');
  const [checking, setChecking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const known = anyRemembered();

  async function unlock() {
    setChecking(true);
    setProblem(null);
    const answer = await unlockOffline(pin);
    setChecking(false);
    setPin('');
    if ('who' in answer) {
      setLocked(false);
    } else if ('wait' in answer) {
      setProblem(`Too many wrong PINs. Try again in ${waitWords(answer.wait)}.`);
    } else {
      setProblem(
        'That PIN does not open this tablet without a connection. Only PINs used on it while it was online do.',
      );
    }
  }

  return (
    <div className="lock" role="dialog" aria-modal="true" aria-labelledby="lock-title">
      <div className="lock__card">
        <h1 id="lock-title" className="lock__title">
          Who is using the till?
        </h1>
        {known ? (
          <>
            <p className="lock__sub">
              No connection. Enter your PIN: one you have used on this tablet before works.
            </p>
            <div
              className="pin__dots"
              aria-live="polite"
              aria-label={`${pin.length} digits entered`}
            >
              {Array.from({ length: Math.max(4, pin.length) }, (_, i) => (
                <span key={i} className={`pin__dot${i < pin.length ? ' is-on' : ''}`} />
              ))}
            </div>

            <NumberPad
              value={pin}
              decimals={0}
              label="PIN"
              onChange={(next) => setPin(next.replace(/\D/g, '').slice(0, PIN_MAX))}
            />

            {problem && (
              <p className="lock__problem" role="alert">
                {problem}
              </p>
            )}

            <Button
              variant="primary"
              size="lg"
              block
              loading={checking}
              disabled={pin.length < 4}
              onClick={() => void unlock()}
            >
              Unlock
            </Button>
          </>
        ) : (
          <p className="lock__problem" role="status">
            No PIN has been used on this tablet yet, so there is none it can check without a
            connection. The till unlocks with any PIN as soon as the line is back.
          </p>
        )}
      </div>
    </div>
  );
}

function waitWords(seconds: number): string {
  if (seconds < 60) return `${seconds} seconds`;
  const minutes = Math.ceil(seconds / 60);
  return minutes === 1 ? 'a minute' : `${minutes} minutes`;
}
